import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, LinearProgress, Paper, Stack, Step, StepLabel, Stepper, Typography, } from "@mui/material";
import DoneIcon from "@mui/icons-material/Done";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import RotateRightIcon from "@mui/icons-material/RotateRight";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useTranslate } from "react-admin";
var terminalStatuses = [
    "COMPLETED",
    "COMPLETED_WITH_ERRORS",
    "FAILED",
];
export var isTerminalStatus = function (status) {
    return Boolean(status && terminalStatuses.includes(status));
};
export var isActiveStatus = function (status) {
    return status === "UPLOADING" || status === "UPLOADED" || status === "QUEUED" || status === "PROCESSING";
};
export var StatusChip = function (_a) {
    var status = _a.status;
    var translate = useTranslate();
    if (!status)
        return null;
    var normalizedStatus = status.toUpperCase().replace(/\s+/g, "_");
    var isActiveProcessing = normalizedStatus === "PROCESSING" ||
        normalizedStatus === "UPLOADING" ||
        normalizedStatus === "QUEUED" ||
        normalizedStatus === "IN_PROGRESS";
    var processingIconSx = isActiveProcessing
        ? {
            animation: "bulkStatusSpin 1.2s linear infinite",
            "@keyframes bulkStatusSpin": {
                from: { transform: "rotate(0deg)" },
                to: { transform: "rotate(360deg)" },
            },
        }
        : undefined;
    var backgroundColor = "#8c8c8c";
    var icon = (_jsx(HourglassEmptyIcon, { fontSize: "small", sx: processingIconSx }));
    if (normalizedStatus === "COMPLETED" || normalizedStatus === "SUCCESS") {
        backgroundColor = "#156319";
        icon = _jsx(DoneIcon, { fontSize: "small" });
    }
    else if (isActiveProcessing) {
        icon = _jsx(RotateRightIcon, { fontSize: "small", sx: processingIconSx });
    }
    else if (normalizedStatus === "COMPLETED_WITH_ERRORS") {
        backgroundColor = "#b26a00";
        icon = _jsx(WarningAmberIcon, { fontSize: "small" });
    }
    else if (normalizedStatus === "FAILED") {
        backgroundColor = "#c62828";
        icon = _jsx(ErrorOutlineIcon, { fontSize: "small" });
    }
    else if (normalizedStatus === "PENDING") {
        backgroundColor = "#8c8c8c";
        icon = _jsx(HourglassEmptyIcon, { fontSize: "small" });
    }
    var examStatusKey = "params.exams.status.".concat(status);
    var examStatusLabel = translate(examStatusKey, { _: "" });
    var label = examStatusLabel && examStatusLabel !== examStatusKey
        ? examStatusLabel
        : translate("pages.bulkGeneration.status.".concat(normalizedStatus), {
            _: status,
        });
    return (_jsx(Button, { disabled: true, size: "small", className: "mid-text-button", startIcon: icon, sx: {
            minWidth: 0,
            backgroundColor: backgroundColor,
            color: "white !important",
            "&.Mui-disabled": {
                backgroundColor: backgroundColor,
                color: "white",
            },
        }, children: label }));
};
var SegmentedProgress = function (_a) {
    var value = _a.value, _b = _a.color, color = _b === void 0 ? "primary" : _b;
    return (_jsxs(Box, { position: "relative", children: [_jsx(LinearProgress, { variant: "determinate", value: Math.max(0, Math.min(100, value)), color: color, sx: { height: 8, borderRadius: 1 } }), [25, 50, 75].map(function (position) { return (_jsx(Box, { sx: {
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: "".concat(position, "%"),
                    width: "1px",
                    bgcolor: "background.paper",
                    opacity: 0.9,
                } }, position)); })] }));
};
var ProgressCountSummary = function (_a) {
    var total = _a.total, success = _a.success, inProgress = _a.inProgress, fail = _a.fail, percent = _a.percent, _b = _a.failedStatus, failedStatus = _b === void 0 ? false : _b;
    var translate = useTranslate();
    var counts = [
        {
            key: "total",
            label: translate("pages.bulkGeneration.progress.total"),
            value: total,
            color: "text.primary",
        },
        {
            key: "success",
            label: translate("pages.bulkGeneration.progress.success"),
            value: success,
            color: "success.main",
        },
        {
            key: "inProgress",
            label: translate("pages.bulkGeneration.progress.inProgress"),
            value: inProgress,
            color: "primary.main",
        },
        {
            key: "fail",
            label: translate("pages.bulkGeneration.progress.fail"),
            value: fail,
            color: "error.main",
        },
    ];
    return (_jsxs(Stack, { spacing: 1.5, children: [_jsx(Box, { children: _jsx(SegmentedProgress, { value: percent, color: failedStatus ? "error" : "primary" }) }), _jsx(Stack, { direction: "row", spacing: 1.5, flexWrap: "wrap", useFlexGap: true, children: counts.map(function (item) { return (_jsxs(Box, { sx: {
                        minWidth: 120,
                        flex: "1 1 120px",
                        px: 1.5,
                        py: 1,
                        borderRadius: 1,
                        bgcolor: "grey.50",
                        border: "1px solid",
                        borderColor: "divider",
                    }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", display: "block", children: item.label }), _jsx(Typography, { variant: "h6", fontWeight: 700, color: item.color, children: item.value })] }, item.key)); }) })] }));
};
var BatchProgress = function (_a) {
    var _b, _c, _d, _e, _f;
    var batch = _a.batch, reportJob = _a.reportJob, activeStage = _a.activeStage, onStageChange = _a.onStageChange;
    var translate = useTranslate();
    var importComplete = isTerminalStatus(batch === null || batch === void 0 ? void 0 : batch.import_status);
    var importPercent = ((_b = batch === null || batch === void 0 ? void 0 : batch.import_progress) === null || _b === void 0 ? void 0 : _b.percent) || 0;
    return (_jsxs(Paper, { variant: "outlined", sx: {
            p: 2.5,
            borderRadius: 2,
            background: "linear-gradient(135deg, #ffffff 0%, #f7faff 100%)",
        }, children: [_jsx(Stepper, { activeStep: activeStage, alternativeLabel: true, nonLinear: true, children: ["upload", "preview", "generate"].map(function (stage, index) { return (_jsx(Step, { disabled: index > 0 && !importComplete, completed: stage === "upload"
                        ? importComplete
                        : stage === "preview"
                            ? Boolean(reportJob)
                            : isTerminalStatus(reportJob === null || reportJob === void 0 ? void 0 : reportJob.status), children: _jsx(StepLabel, { onClick: function () {
                            if (index === 0 || importComplete)
                                onStageChange(index);
                        }, sx: { cursor: index === 0 || importComplete ? "pointer" : "default" }, children: translate("pages.bulkGeneration.stages.".concat(stage)) }) }, stage)); }) }), batch && activeStage === 0 && (_jsxs(Stack, { spacing: 1.5, mt: 2.5, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsx(Typography, { variant: "body2", fontWeight: 600, children: translate("pages.bulkGeneration.progress.import") }), _jsx(StatusChip, { status: batch.import_status })] }), _jsx(ProgressCountSummary, { total: ((_c = batch.import_progress) === null || _c === void 0 ? void 0 : _c.total) || 0, success: ((_d = batch.import_progress) === null || _d === void 0 ? void 0 : _d.success) || 0, inProgress: ((_e = batch.import_progress) === null || _e === void 0 ? void 0 : _e.in_progress) || 0, fail: ((_f = batch.import_progress) === null || _f === void 0 ? void 0 : _f.failed) || 0, percent: isTerminalStatus(batch.import_status) && importPercent === 0
                            ? 100
                            : importPercent, failedStatus: batch.import_status === "FAILED" }), batch.import_error && (_jsx(Typography, { variant: "body2", color: "error", children: batch.import_error }))] })), reportJob && activeStage === 2 && (_jsxs(Stack, { spacing: 1.5, mt: 3, children: [_jsxs(Stack, { direction: "row", justifyContent: "space-between", alignItems: "center", children: [_jsx(Typography, { variant: "body2", fontWeight: 600, children: translate("pages.bulkGeneration.progress.report") }), _jsx(StatusChip, { status: reportJob.status })] }), _jsx(ProgressCountSummary, { total: reportJob.total_tasks, success: reportJob.success_tasks, inProgress: reportJob.queued_tasks + reportJob.processing_tasks, fail: reportJob.failed_tasks, percent: reportJob.progress_percent || 0, failedStatus: reportJob.status === "FAILED" }), reportJob.error && (_jsx(Typography, { variant: "body2", color: "error", children: reportJob.error }))] }))] }));
};
export default BatchProgress;
